import { 
  getAuth, 
  fetchSignInMethodsForEmail,
  linkWithPopup,
  linkWithCredential,
  signInWithCredential,
  sendPasswordResetEmail, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  signOut,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  MultiFactorResolver,
  MultiFactorError,
  OAuthProvider,
  sendEmailVerification,
  applyActionCode
} from 'firebase/auth'
import { RegisterUserType } from '../../types/auth/auth'
import api, { getApiErrorMessage } from '../api'

export const fbauth = getAuth()
export let role = " "

const createMicrosoftProvider = () => {
  const provider = new OAuthProvider('microsoft.com')
  provider.setCustomParameters({
    prompt: 'select_account',
  })
  provider.addScope('openid')
  provider.addScope('profile')
  provider.addScope('email')
  return provider
}

export const login = async ({ username, password }: { username: string; password: string }) => {
  try {
    await signInWithEmailAndPassword(fbauth, username, password).catch((error) => {
      if (error.code === 'auth/multi-factor-auth-required') {
        // MFA is required, throw the error to be handled by the caller
        throw error
      } else if (error.code === 'auth/invalid-credential') {
        throw Error('Incorrect username/password')
      } else if (error.code === 'auth/too-many-requests') {
        throw Error('Too many login attempts, please try again later')
      }
    })
  } catch (error) {
    throw error
  }
  return fbauth.currentUser
}

export const loginWithMicrosoft = async () => {
  const provider = createMicrosoftProvider()
  await signInWithPopup(fbauth, provider)
  return fbauth.currentUser
}

export const linkCurrentUserWithMicrosoft = async () => {
  const user = fbauth.currentUser
  if (!user) {
    throw new Error('You need to be signed in before linking Microsoft.')
  }

  const provider = createMicrosoftProvider()
  await linkWithPopup(user, provider)
  await user.reload()
  return user
}

export const linkMicrosoftToExistingPasswordUser = async ({
  error,
  email,
  password,
}: {
  error: unknown
  email?: string
  password?: string
}) => {
  const pendingCredential = OAuthProvider.credentialFromError(error as any)
  if (!pendingCredential) {
    throw error
  }

  const resolvedEmail = (email || (error as any)?.customData?.email || '').trim().toLowerCase()
  if (!resolvedEmail) {
    throw new Error('This Microsoft account is already registered with a different sign-in method.')
  }

  const methods = await fetchSignInMethodsForEmail(fbauth, resolvedEmail)
  if (!methods.includes('password')) {
    throw new Error('This account already exists with a different sign-in method. Sign in with that method first, then try Microsoft again.')
  }

  if (!password) {
    throw new Error('This email already has a password account. Sign in once with your password to link Microsoft.')
  }

  const userCredential = await signInWithEmailAndPassword(fbauth, resolvedEmail, password)
  await linkWithCredential(userCredential.user, pendingCredential)
  return userCredential.user
}

export const linkInviteWithMicrosoft = async (inviteCode: string, profileData?: FormData) => {
  const { initializeApp, deleteApp } = await import('firebase/app')
  const {
    getAuth: getAuthInstance,
    OAuthProvider: TempOAuthProvider,
    signInWithPopup: signInWithPopupInstance,
    signOut: signOutInstance,
  } = await import('firebase/auth')

  const tempApp = initializeApp(
    {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    },
    `invite-link-${Date.now()}`
  )

  try {
    const tempAuth = getAuthInstance(tempApp)
    const provider = createMicrosoftProvider()

    const userCredential = await signInWithPopupInstance(tempAuth, provider)
    const idToken = await userCredential.user.getIdToken(true)

    await linkInviteTokenWithFirebaseToken(inviteCode, idToken, profileData)

    const credential = TempOAuthProvider.credentialFromResult(userCredential)
    if (!credential) {
      throw new Error('Unable to complete Microsoft sign-in. Please try again.')
    }

    await signInWithCredential(fbauth, credential)
    return fbauth.currentUser
  } finally {
    try {
      const tempAuth = getAuthInstance(tempApp)
      await signOutInstance(tempAuth)
    } catch {
      // Ignore cleanup errors
    }

    try {
      await deleteApp(tempApp)
    } catch {
      // Ignore cleanup errors
    }
  }
}

export const linkInviteTokenWithFirebaseToken = async (inviteCode: string, idToken: string, profileData?: FormData) => {
  const payload = profileData ?? {}
  const headers: Record<string, string> = {
    Authorization: `Bearer ${idToken}`,
  }

  if (profileData instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data'
  }

  await api.post(`/user/invite/${inviteCode}/link/`, payload, { headers })
}

// Initialize (or reuse) an invisible RecaptchaVerifier for MFA enrollment

export const initRecaptchaVerifier = async () => {
  try {
    // Clear any existing recaptcha verifier
    const container = document.getElementById('recaptcha-container')
    if (container) {
      container.innerHTML = ''
    }

    // Always create a new verifier bound to the current DOM element
    const verifier = new RecaptchaVerifier(
      fbauth,
      'recaptcha-container',
      { 
        size: 'invisible',
        callback: () => {
        },
        'expired-callback': () => {
        }
      },
    )

    await verifier.render()
    return verifier
  } catch (error) {
    console.error('[reCAPTCHA] Failed to initialize:', error)
    throw error
  }
}

// Enroll user in MFA
export const enrollMFA = async (phoneNumber: string, recaptchaVerifier: RecaptchaVerifier) => {
  const user = fbauth.currentUser
  if (!user) throw new Error('No user is signed in')

  // Reload user to refresh emailVerified status from Firebase servers
  await user.reload()
  
  if (!user.emailVerified) {
    throw new Error('Email must be verified before enrolling in MFA')
  }

  // Ensure phone number is in E.164 format (+[country code][number])
  const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`
  const session = await multiFactor(user).getSession()
  const phoneAuthProvider = new PhoneAuthProvider(fbauth)
  
  try {
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      {
        phoneNumber: formattedPhone,
        session,
      },
      recaptchaVerifier
    )
    
    return verificationId
  } catch (error: any) {
    console.error('[MFA] enrollMFA error:', error)
    // Provide more detailed error message
    if (error.code === 'auth/invalid-phone-number') {
      throw new Error(`Invalid phone number format. Please use E.164 format (e.g., +1234567890)`)
    } else if (error.code === 'auth/invalid-app-credential') {
      throw new Error('Firebase app credential error. Please check your Firebase configuration and ensure reCAPTCHA is properly set up.')
    }
    throw error
  }
}

// Complete MFA enrollment
export const completeMFAEnrollment = async (verificationId: string, verificationCode: string, displayName?: string) => {
  const user = fbauth.currentUser
  if (!user) throw new Error('No user is signed in')

  const cred = PhoneAuthProvider.credential(verificationId, verificationCode)
  const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
  
  await multiFactor(user).enroll(multiFactorAssertion, displayName)
}

// Initialize MFA verification during sign-in (sends SMS)
export const initMfaSignInVerification = async (resolver: MultiFactorResolver, selectedIndex: number = 0) => {
  
  try {
    const phoneAuthProvider = new PhoneAuthProvider(fbauth)
    
    // Clear any existing recaptcha container
    const container = document.getElementById('recaptcha-container-mfa')
    if (container) {
      container.innerHTML = ''
    }
    
    const recaptchaVerifier = new RecaptchaVerifier(fbauth, 'recaptcha-container-mfa', {
      size: 'invisible',
    })
    
    await recaptchaVerifier.render()
  
    const phoneInfoOptions = {
      multiFactorHint: resolver.hints[selectedIndex],
      session: resolver.session,
    }
    
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
    return verificationId
  } catch (error) {
    console.error('[MFA API] initMfaSignInVerification error:', error)
    throw error
  }
}

// Verify MFA during sign-in
export const verifyMFA = async (resolver: MultiFactorResolver, verificationId: string, verificationCode: string) => {
  const cred = PhoneAuthProvider.credential(verificationId, verificationCode)
  const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
  
  await resolver.resolveSignIn(multiFactorAssertion)
}

// Unenroll from MFA
export const unenrollMFA = async (factorUid: string) => {
  const user = fbauth.currentUser
  if (!user) throw new Error('No user is signed in')
  
  const enrolledFactors = multiFactor(user).enrolledFactors
  const factorToUnenroll = enrolledFactors.find(factor => factor.uid === factorUid)
  
  if (factorToUnenroll) {
    await multiFactor(user).unenroll(factorToUnenroll)
  } else {
    throw new Error('MFA factor not found')
  }
}

// Get enrolled MFA factors
export const getEnrolledMFAFactors = () => {
  const user = fbauth.currentUser
  if (!user) return []
  
  return multiFactor(user).enrolledFactors
}

export const logout = async () => {
  await signOut(fbauth)
}

export const auth = async () => {
  const res = await api.get('/user/self/')
  role = res.data.role
  return res.data
}

export const registerUser = async (user: RegisterUserType | FormData) => {
  const headers = user instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
  let res = await api.post('/user/self/', user, { headers })
  
  // Extract password for return value
  const password = user instanceof FormData ? user.get('password') as string : user.password
  return { ...res.data, password }
}

export const validateInviteCode = async (inviteCode: string) => {
  try {
    const res = await api.get(`/user/invite/${inviteCode}/`)
    return { valid: true, data: res.data }
  } catch (error: unknown) {
    return { valid: false, error: getApiErrorMessage(error) }
  }
}

export const inviteUser = async (user: RegisterUserType | FormData, inviteCode: string) => {
  const headers = user instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {}
  let res = await api.put(`/user/invite/${inviteCode}/`, user, { headers })
  
  // Extract password for return value
  const password = user instanceof FormData ? user.get('password') as string : user.password
  return { ...res.data, password }
}

export const updateUser = async (user: any) => {
  const res = await api.patch('/user/self/', user)
  return res.data
}

export const resetPassword = async (email: string) => {
  await sendPasswordResetEmail(fbauth, email)
}

export const changePassword = async (data: { password: string }) => {
  const response = await api.patch('/user/change_password/', data)
  return response.data
}

export const requestOTP = async (data: { email: string }) => {
  const token = await fbauth.currentUser?.getIdToken(true)
  const response = await api.post('/user/email_verification/send_verification_code/', data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

export const verifyOTP = async (data: { email: string; otp: string }) => {
  const token = await fbauth.currentUser?.getIdToken(true) // Force refresh the token
  const response = await api.post(`/user/email_verification/${data.otp}/verify/`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

// Firebase Email Verification
export const sendFirebaseEmailVerification = async () => {
  const user = fbauth.currentUser
  if (!user) throw new Error('No user is signed in')
  
  await sendEmailVerification(user)
}

export const checkEmailVerified = async () => {
  const user = fbauth.currentUser
  if (!user) {
    return false
  }
  
  try {
    // Reload user to get fresh emailVerified status from server
    await user.reload()
    return user.emailVerified
  } catch (error) {
    console.error('[Email Verification] Error reloading user:', error)
    throw error
  }
}

export const applyEmailVerificationCode = async (actionCode: string) => {
  await applyActionCode(fbauth, actionCode)

  // Reload user to update emailVerified status
  const user = fbauth.currentUser
  if (user) {
    await user.reload()
  }
}

// MFA Reset (email-based)
export const requestMfaReset = async (email: string) => {
  await api.post('/user/mfa-reset/request/', { email })
}

export const confirmMfaReset = async (token: string): Promise<{ success: boolean; custom_token?: string }> => {
  const res = await api.post('/user/mfa-reset/confirm/', { token })
  return res.data
}

/**
 * Sign in with a Firebase custom token (used during MFA reset to bypass MFA
 * and unenroll all factors client-side).
 *
 * Uses a SECONDARY Firebase app instance so the primary app's auth state
 * (the admin's session) is not affected. This prevents the admin from
 * being logged out when resetting MFA for another user.
 */
export const signInAndClearMfa = async (customToken: string) => {
  const { initializeApp, deleteApp } = await import('firebase/app')
  const { getAuth: getAuthInstance, signInWithCustomToken, signOut: signOutInstance, multiFactor: mfInstance } = await import('firebase/auth')

  // Create a temporary secondary Firebase app
  const tempApp = initializeApp(
    {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    },
    `mfa-reset-${Date.now()}`
  )

  try {
    const tempAuth = getAuthInstance(tempApp)
    const userCred = await signInWithCustomToken(tempAuth, customToken)
    const user = userCred.user

    // Unenroll all MFA factors
    const factors = mfInstance(user).enrolledFactors
    for (const factor of factors) {
      await mfInstance(user).unenroll(factor)
    }

    await signOutInstance(tempAuth)
  } finally {
    // Clean up the temporary app
    try {
      await deleteApp(tempApp)
    } catch {
      // Ignore cleanup errors
    }
  }
}
